import { z } from 'zod';
import { AccountType, AssetCategory, AssetSubType, ValuationSource, ValueBehaviour, DepreciationMethod, LoanPurpose, ACCOUNT_TYPE_REQUIREMENTS } from '../types/account.types';

/**
 * Account Validation Utilities
 * 
 * Zod schemas and validation functions for account data
 * Following the validation rules from the specification
 */

// Base account validation schema
const baseAccountSchema = z.object({
  name: z.string()
    .min(1, 'Account name is required')
    .max(100, 'Account name must be 100 characters or less')
    .regex(/^[a-zA-Z0-9\s]+$/, 'Account name can only contain letters, numbers, and spaces'),
  
  type: z.nativeEnum(AccountType, {
    message: 'Invalid account type'
  }),
  
  provider: z.string()
    .min(1, 'Provider is required')
    .max(100, 'Provider name must be 100 characters or less')
    .regex(/^[a-zA-Z0-9\s\-\.&]+$/, 'Provider name contains invalid characters'),
  
  accountNumber: z.string()
    .regex(/^[0-9]{4}$/, 'Account number must be exactly 4 digits'),
  
  openingBalance: z.number()
    .finite('Opening balance must be a valid number'),
  
  openingDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Opening date must be in YYYY-MM-DD format')
    .refine((date) => {
      const dateObj = new Date(date);
      const today = new Date();
      const tenYearsAgo = new Date();
      tenYearsAgo.setFullYear(today.getFullYear() - 10);
      
      return dateObj <= today && dateObj >= tenYearsAgo;
    }, 'Opening date cannot be in the future or more than 10 years ago'),
  
  includeInNetWorth: z.boolean().optional().default(true),
  
  notes: z.string()
    .max(500, 'Notes must be 500 characters or less')
    .optional()
});

// Type-specific field schemas
const creditCardFieldsSchema = z.object({
  creditLimit: z.number()
    .positive('Credit limit must be positive'),
  paymentDueDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Payment due date must be in YYYY-MM-DD format'),
  interestRate: z.number()
    .min(0, 'Interest rate cannot be negative')
    .max(100, 'Interest rate cannot exceed 100%')
    .optional(),
  renewalDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Renewal date must be in YYYY-MM-DD format')
    .refine((date) => new Date(date) > new Date(), 'Renewal date must be in the future')
    .optional()
});

const loanFieldsSchema = z.object({
  loanAmount: z.number()
    .finite('Loan amount must be a valid number')
    .min(0, 'Loan amount cannot be negative'),
  interestRate: z.number()
    .min(0, 'Interest rate cannot be negative')
    .max(100, 'Interest rate cannot exceed 100%'),
  paymentDueDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Payment due date must be in YYYY-MM-DD format'),
  lastPaymentDue: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Last payment due date must be in YYYY-MM-DD format')
    .optional(),
  loanPurpose: z.nativeEnum(LoanPurpose).optional(),
  linkedAccountId: z.string().uuid('Linked account must be a valid ID').optional().nullable(),
  leaseEndDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Lease end date must be in YYYY-MM-DD format')
    .optional(),
  residualValue: z.number()
    .finite('Residual value must be a valid number')
    .min(0, 'Residual value cannot be negative')
    .optional(),
  employerName: z.string()
    .max(100, 'Employer name must be 100 characters or less')
    .optional()
});

const savingsFieldsSchema = z.object({
  interestRate: z.number()
    .min(0, 'Interest rate cannot be negative')
    .max(100, 'Interest rate cannot exceed 100%')
    .optional(),
  interestCalculatedDay: z.number()
    .int('Interest calculated day must be a whole number')
    .min(1, 'Interest calculated day must be between 1 and 31')
    .max(31, 'Interest calculated day must be between 1 and 31')
    .optional()
});

const investmentFieldsSchema = z.object({
  interestRate: z.number()
    .min(0, 'Interest rate cannot be negative')
    .max(100, 'Interest rate cannot exceed 100%')
    .optional()
});

// Superannuation fields schema (currently unused, reserved for future use)
// const superannuationFieldsSchema = z.object({
//   interestRate: z.number()
//     .min(0, 'Interest rate cannot be negative')
//     .max(100, 'Interest rate cannot exceed 100%')
//     .optional()
// });

// Asset fields schema with category/sub-type validation
// Note: Transform is applied after merge to avoid type issues with ZodPipe
const assetFieldsSchemaBase = z.object({
  category: z.nativeEnum(AssetCategory, {
    message: 'Invalid asset category'
  }),
  subType: z.nativeEnum(AssetSubType, {
    message: 'Invalid asset sub-type'
  }),
  currentValue: z.number()
    .finite('Current value must be a valid number'),
  purchasePrice: z.number()
    .finite('Purchase price must be a valid number')
    .optional(),
  purchaseDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Purchase date must be in YYYY-MM-DD format')
    .refine((date) => {
      const dateObj = new Date(date);
      const today = new Date();
      return dateObj <= today;
    }, 'Purchase date cannot be in the future')
    .optional(),
  valuationSource: z.nativeEnum(ValuationSource, {
    message: 'Invalid valuation source'
  }).optional().default(ValuationSource.MANUAL),
  valuationProvider: z.string()
    .max(255, 'Valuation provider must be 255 characters or less')
    .optional(),
  lastValuationDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Last valuation date must be in YYYY-MM-DD format')
    .optional(),
  // v2 fields (optional for now)
  valueBehaviour: z.nativeEnum(ValueBehaviour).optional(),
  depreciationMethod: z.nativeEnum(DepreciationMethod).optional(),
  depreciationRatePercent: z.number()
    .min(0, 'Depreciation rate cannot be negative')
    .max(100, 'Depreciation rate cannot exceed 100%')
    .optional(),
  autoUpdateValue: z.boolean().optional().default(false)
}).refine((data) => {
  // Validate sub-type matches category
  const validSubTypes: Record<AssetCategory, AssetSubType[]> = {
    [AssetCategory.PROPERTY]: [AssetSubType.HOUSE, AssetSubType.APARTMENT, AssetSubType.LAND, AssetSubType.OTHER],
    [AssetCategory.VEHICLE]: [AssetSubType.CAR, AssetSubType.MOTORBIKE, AssetSubType.BOAT, AssetSubType.OTHER],
    [AssetCategory.VALUABLES]: [AssetSubType.JEWELLERY, AssetSubType.ART, AssetSubType.COLLECTIBLES, AssetSubType.OTHER],
    [AssetCategory.OTHER]: [AssetSubType.OTHER]
  };
  
  return validSubTypes[data.category]?.includes(data.subType) ?? false;
}, {
  message: 'Sub-type does not match the selected category',
  path: ['subType']
}).refine((data) => {
  // If purchaseDate is provided, lastValuationDate should default to purchaseDate if not provided
  if (data.purchaseDate && !data.lastValuationDate) {
    return true; // Will be set in service layer
  }
  return true;
});

// Create account validation schema
export const createAccountSchema = z.discriminatedUnion('type', [
  baseAccountSchema.extend({
    type: z.literal(AccountType.BANK_CASH)
  }),
  baseAccountSchema.extend({
    type: z.literal(AccountType.SAVINGS)
  }).merge(savingsFieldsSchema),
  baseAccountSchema.extend({
    type: z.literal(AccountType.CREDIT_CARD)
  }).merge(creditCardFieldsSchema),
  baseAccountSchema.extend({
    type: z.literal(AccountType.LOAN)
  }).merge(loanFieldsSchema),
  baseAccountSchema.extend({
    type: z.literal(AccountType.INVESTMENTS_SUPERANNUATION)
  }).merge(investmentFieldsSchema),
  // For assets, override accountNumber, openingBalance, openingDate, and provider validation
  baseAccountSchema.extend({
    type: z.literal(AccountType.ASSETS),
    provider: z.string()
      .max(100, 'Provider name must be 100 characters or less')
      .regex(/^[a-zA-Z0-9\s\-\.&]*$/, 'Provider name contains invalid characters')
      .optional(),
    accountNumber: z.string()
      .regex(/^[0-9]{4}$/, 'Account number must be exactly 4 digits')
      .optional()
      .default('0000'),
    openingBalance: z.number()
      .finite('Opening balance must be a valid number')
      .optional()
      .default(0),
    // For assets, openingDate is optional (purchaseDate from assetFieldsSchema is used instead)
    // This allows CreateAssetRequest to use purchaseDate without requiring openingDate
    openingDate: z.string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Opening date must be in YYYY-MM-DD format')
      .refine((date) => {
        const dateObj = new Date(date);
        const today = new Date();
        return dateObj <= today;
      }, 'Opening date cannot be in the future')
      .optional()
  }).merge(assetFieldsSchemaBase).transform((data) => {
    // Set category-based default for valueBehaviour per FR-022
    // Property → appreciates, Vehicle → depreciates, Valuables/Other → mixed
    if (data.type === AccountType.ASSETS && !data.valueBehaviour && data.category) {
      const defaultBehaviourMap: Record<AssetCategory, ValueBehaviour> = {
        [AssetCategory.PROPERTY]: ValueBehaviour.APPRECIATES,
        [AssetCategory.VEHICLE]: ValueBehaviour.DEPRECIATES,
        [AssetCategory.VALUABLES]: ValueBehaviour.MIXED,
        [AssetCategory.OTHER]: ValueBehaviour.MIXED
      };
      data.valueBehaviour = defaultBehaviourMap[data.category] || ValueBehaviour.MIXED;
    }
    return data;
  }).refine((data) => {
    // For assets, either openingDate or purchaseDate must be provided
    // If purchaseDate is provided, it will be used; otherwise openingDate is required
    if (data.type === AccountType.ASSETS) {
      if (!data.purchaseDate && !data.openingDate) {
        return false;
      }
    }
    return true;
  }, {
    message: 'Either openingDate or purchaseDate must be provided for assets',
    path: ['openingDate']
  })
]);

// Update account validation schema
export const updateAccountSchema = z.object({
  name: z.string()
    .min(1, 'Account name is required')
    .max(100, 'Account name must be 100 characters or less')
    .regex(/^[a-zA-Z0-9\s]+$/, 'Account name can only contain letters, numbers, and spaces')
    .optional(),

  type: z.nativeEnum(AccountType).optional(),
  
  provider: z.string()
    .min(1, 'Provider is required')
    .max(100, 'Provider name must be 100 characters or less')
    .regex(/^[a-zA-Z0-9\s\-\.&]+$/, 'Provider name contains invalid characters')
    .optional(),
  
  accountNumber: z.string()
    .regex(/^[0-9]{4}$/, 'Account number must be exactly 4 digits')
    .optional(),

  openingBalance: z.number()
    .finite('Opening balance must be a valid number')
    .optional(),

  currentBalance: z.number()
    .finite('Current balance must be a valid number')
    .optional(),

  // Format only here; 10-year limit applied in superRefine for non-asset accounts
  openingDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Opening date must be in YYYY-MM-DD format')
    .optional(),

  isActive: z.boolean().optional(),
  closureDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Closure date must be in YYYY-MM-DD format')
    .optional(),
  closureReason: z.string()
    .max(500, 'Closure reason must be 500 characters or less')
    .optional(),
  
  includeInNetWorth: z.boolean().optional(),
  
  notes: z.string()
    .max(500, 'Notes must be 500 characters or less')
    .optional(),
  
  // Type-specific fields
  creditLimit: z.number()
    .positive('Credit limit must be positive')
    .optional(),
  
  interestRate: z.number()
    .min(0, 'Interest rate cannot be negative')
    .max(100, 'Interest rate cannot exceed 100%')
    .optional(),
  
  paymentDueDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Payment due date must be in YYYY-MM-DD format')
    .optional(),
  
  renewalDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Renewal date must be in YYYY-MM-DD format')
    .refine((date) => new Date(date) > new Date(), 'Renewal date must be in the future')
    .optional(),
  
  loanAmount: z.number()
    .finite('Loan amount must be a valid number')
    .min(0, 'Loan amount cannot be negative')
    .optional(),
  
  lastPaymentDue: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Last payment due date must be in YYYY-MM-DD format')
    .optional(),

  loanPurpose: z.nativeEnum(LoanPurpose).optional(),
  linkedAccountId: z.string().uuid('Linked account must be a valid ID').optional().nullable(),
  leaseEndDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Lease end date must be in YYYY-MM-DD format')
    .optional(),
  residualValue: z.number()
    .finite('Residual value must be a valid number')
    .min(0, 'Residual value cannot be negative')
    .optional(),
  employerName: z.string()
    .max(100, 'Employer name must be 100 characters or less')
    .optional(),
  
  interestCalculatedDay: z.number()
    .int('Interest calculated day must be a whole number')
    .min(1, 'Interest calculated day must be between 1 and 31')
    .max(31, 'Interest calculated day must be between 1 and 31')
    .optional(),
  
  // Asset-specific fields (when type === AccountType.ASSETS)
  category: z.nativeEnum(AssetCategory, {
    message: 'Invalid asset category'
  }).optional(),
  subType: z.nativeEnum(AssetSubType, {
    message: 'Invalid asset sub-type'
  }).optional(),
  currentValue: z.number()
    .finite('Current value must be a valid number')
    .optional(),
  purchasePrice: z.number()
    .finite('Purchase price must be a valid number')
    .optional(),
  purchaseDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Purchase date must be in YYYY-MM-DD format')
    .refine((date) => {
      const dateObj = new Date(date);
      const today = new Date();
      return dateObj <= today;
    }, 'Purchase date cannot be in the future')
    .optional(),
  valuationSource: z.nativeEnum(ValuationSource, {
    message: 'Invalid valuation source'
  }).optional(),
  valuationProvider: z.string()
    .max(255, 'Valuation provider must be 255 characters or less')
    .optional(),
  lastValuationDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Last valuation date must be in YYYY-MM-DD format')
    .optional(),
  // v2 fields (optional for now)
  valueBehaviour: z.nativeEnum(ValueBehaviour).optional(),
  depreciationMethod: z.nativeEnum(DepreciationMethod).optional(),
  depreciationRatePercent: z.number()
    .min(0, 'Depreciation rate cannot be negative')
    .max(100, 'Depreciation rate cannot exceed 100%')
    .optional(),
  autoUpdateValue: z.boolean().optional()
}).superRefine((data, ctx) => {
  // Asset updates: openingDate has no 10-year minimum (purchaseDate is the source of truth)
  const isAssetUpdate =
    data.type === AccountType.ASSETS ||
    data.purchaseDate !== undefined ||
    data.category !== undefined ||
    data.currentValue !== undefined;

  if (data.openingDate) {
    const dateObj = new Date(data.openingDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (dateObj > today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isAssetUpdate
          ? 'Opening date cannot be in the future'
          : 'Opening date cannot be in the future or more than 10 years ago',
        path: ['openingDate']
      });
    } else if (!isAssetUpdate) {
      const tenYearsAgo = new Date();
      tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
      tenYearsAgo.setHours(0, 0, 0, 0);
      if (dateObj < tenYearsAgo) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Opening date cannot be in the future or more than 10 years ago',
          path: ['openingDate']
        });
      }
    }
  }
}).transform((data) => {
  // Set category-based default for valueBehaviour per FR-022 when updating
  // Only set if category is provided and valueBehaviour is not already set
  if (!data.valueBehaviour && data.category) {
    const defaultBehaviourMap: Record<AssetCategory, ValueBehaviour> = {
      [AssetCategory.PROPERTY]: ValueBehaviour.APPRECIATES,
      [AssetCategory.VEHICLE]: ValueBehaviour.DEPRECIATES,
      [AssetCategory.VALUABLES]: ValueBehaviour.MIXED,
      [AssetCategory.OTHER]: ValueBehaviour.MIXED
    };
    data.valueBehaviour = defaultBehaviourMap[data.category] || ValueBehaviour.MIXED;
  }
  return data;
});

// Close account validation schema
export const closeAccountSchema = z.object({
  closureDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Closure date must be in YYYY-MM-DD format')
    .refine((date) => {
      const dateObj = new Date(date);
      const today = new Date();
      return dateObj <= today;
    }, 'Closure date cannot be in the future'),
  
  closureReason: z.string()
    .max(500, 'Closure reason must be 500 characters or less')
    .optional(),

  includeInNetWorth: z.boolean().optional(),
  currentValue: z.number().finite('Current value must be a valid number').optional()
});

// Account list parameters validation schema
export const accountListParamsSchema = z.object({
  type: z.nativeEnum(AccountType).optional(),
  status: z.enum(['active', 'closed']).optional(),
  includeInNetWorth: z.boolean().optional(),
  sortBy: z.enum(['name', 'type', 'balance', 'dateCreated', 'provider', 'currentValue']).optional(),
  category: z.nativeEnum(AssetCategory).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional().default('asc'),
  page: z.number().int().min(1).optional().default(1),
  limit: z.number().int().min(1).max(100).optional().default(20)
});

// Validation functions
export function validateCreateAccount(data: unknown) {
  return createAccountSchema.safeParse(data);
}

export function validateUpdateAccount(data: unknown) {
  return updateAccountSchema.safeParse(data);
}

export function validateCloseAccount(data: unknown) {
  return closeAccountSchema.safeParse(data);
}

export function validateAccountListParams(data: unknown) {
  return accountListParamsSchema.safeParse(data);
}

// Helper function to check if required fields are present for account type
export function validateAccountTypeRequirements(accountType: AccountType, data: any): string[] {
  const errors: string[] = [];
  const requiredFields = ACCOUNT_TYPE_REQUIREMENTS[accountType];
  
  for (const field of requiredFields) {
    if (data[field] === undefined || data[field] === null || data[field] === '') {
      errors.push(`${field} is required for ${accountType} accounts`);
    }
  }
  
  return errors;
}

// Helper function to validate account number uniqueness (would need database access)
export function validateAccountNumberUniqueness(_accountNumber: string, _userId: string): Promise<boolean> {
  // This would typically check against a database
  // For now, return true as a placeholder
  return Promise.resolve(true);
}

// Helper function to validate provider name uniqueness (case-insensitive)
export function validateProviderNameUniqueness(_provider: string, _userId: string): Promise<boolean> {
  // This would typically check against a database
  // For now, return true as a placeholder
  return Promise.resolve(true);
}
